import { useTheme } from '@mui/material';
import { useMemo } from 'react';

import { type DiagramColorOption } from '@/components/diagrams/components/DiagramStylePickers/DiagramColorPicker';
import { getTilePalette } from '@/components/diagrams/components/Tile/Tile.utils';
import {
  DiagramNodeColor,
  isDiagramHexColor,
} from '@/components/diagrams/diagramStyle';
import { TRUST_CONTAINER_VARIANT_TITLE } from '@/modules/estate/EstateTrusts/TrustContainer/TrustContainer.types';
import { COLORS } from '@/styles/tokens/colors';

/**
 * The colors of the node and line color menus, in menu order. A node saves
 * the tile palette token, so it follows the tenant theme. The hue of a token
 * differs between tenants, so the menu names each token by the estate
 * category that uses it, as the other diagrams do. Gray and Orange are the
 * same in every theme, so the menu names them by color. Orange has no
 * confirmed token yet, so it saves a hex color.
 */
const DIAGRAM_COLORS = [
  {
    nodeColor: DiagramNodeColor.Primary,
    label: TRUST_CONTAINER_VARIANT_TITLE['in-estate'],
  },
  {
    nodeColor: DiagramNodeColor.Secondary,
    label: TRUST_CONTAINER_VARIANT_TITLE['out-of-estate-family'],
  },
  {
    nodeColor: DiagramNodeColor.Tertiary,
    label: TRUST_CONTAINER_VARIANT_TITLE['out-of-estate-charity'],
  },
  { nodeColor: DiagramNodeColor.Default, label: 'Gray' },
  { nodeColor: COLORS.ORANGE[400], label: 'Orange' },
] as const;

/** The main color of the default tile palette, which is not in the theme. */
const DEFAULT_NODE_SWATCH = COLORS.GRAY[300];

/** The color of a line without a saved color. */
export const LINE_DEFAULT_COLOR = COLORS.GRAY[500];

/**
 * Returns the options of the node and line color menus, with the swatches of
 * the current theme. Both menus have the same colors. A line color cannot be
 * a tile palette token, so a line saves the hex color of the token in the
 * current theme. Gray saves the default line gray, which is darker than the
 * gray tile palette so that the line stays visible.
 */
export function useDiagramColorOptions(): {
  nodeColorOptions: readonly DiagramColorOption[];
  lineColorOptions: readonly DiagramColorOption[];
} {
  const theme = useTheme();
  return useMemo(() => {
    const colors = DIAGRAM_COLORS.map(({ nodeColor, label }) => ({
      nodeColor,
      label,
      swatch: isDiagramHexColor(nodeColor)
        ? nodeColor
        : nodeColor === DiagramNodeColor.Default
          ? DEFAULT_NODE_SWATCH
          : (getTilePalette(theme, nodeColor)?.main ?? DEFAULT_NODE_SWATCH),
    }));
    return {
      nodeColorOptions: colors.map(({ nodeColor, label, swatch }) => ({
        value: nodeColor,
        label,
        swatch,
      })),
      lineColorOptions: colors.map(({ nodeColor, label, swatch }) => {
        const value =
          nodeColor === DiagramNodeColor.Default ? LINE_DEFAULT_COLOR : swatch;
        return { value, label, swatch: value };
      }),
    };
  }, [theme]);
}
